import PropTypes from 'prop-types';
import { Box } from '@mui/material';

const APP_STORE_URL =
  'https://itunes.apple.com/WebObjects/MZStore.woa/wa/redirectToContent?id=6751509285&mt=8';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.funizzle.mobile.app';

const APP_STORE_BADGE =
  'https://developer.apple.com/assets/elements/badges/download-on-the-app-store.svg';
const PLAY_STORE_BADGE =
  'https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png';

/**
 * AppStoreBadges renders both store badges at matched VISUAL size.
 *
 * Google's official Play Store PNG bakes in ~25% transparent safe-area
 * padding per Google's brand spec. So if both badges are sized to the same
 * box dimensions, Apple's tight SVG fills its box edge-to-edge while
 * Google's visible content occupies only ~70% of its box — and looks
 * obviously smaller. Bumping Google's box compensates somewhat but not
 * enough.
 *
 * Solution: both badges share an identical box size, but on the Google
 * badge we apply background-size > 100% (with overflow hidden) so the
 * PNG zooms in past its safe-area padding. The transparent margins spill
 * beyond box edges and the visible badge fills the box, matching Apple.
 */
export default function AppStoreBadges({ sx = {}, scale = 1, children }) {
  // Identical box size for both badges so they line up perfectly.
  const size = {
    width: { xs: 142 * scale, sm: 160 * scale, lg: 180 * scale },
    height: { xs: 48 * scale, sm: 54 * scale, lg: 60 * scale },
  };

  const baseLink = {
    display: 'block',
    width: size.width,
    height: size.height,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    overflow: 'hidden',
    cursor: 'pointer',
    transition: 'transform 0.2s ease-out, filter 0.2s ease-out',
    '&:hover': {
      transform: 'scale(1.06)',
      filter: 'brightness(1.08)',
    },
    '&:active': { transform: 'scale(0.98)' },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: { xs: 1, sm: 1.5 },
        justifyContent: 'center',
        alignItems: 'center',
        flexWrap: 'wrap',
        ...sx,
      }}
    >
      <Box
        component="a"
        href={APP_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Download BingePlay on the App Store"
        sx={{
          ...baseLink,
          backgroundImage: `url(${APP_STORE_BADGE})`,
          backgroundSize: 'contain',
        }}
      />
      {children}
      <Box
        component="a"
        href={PLAY_STORE_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Get BingePlay on Google Play"
        sx={{
          ...baseLink,
          backgroundImage: `url(${PLAY_STORE_BADGE})`,
          // Zoom in 124% horizontally to crop most of Google's
          // baked-in safe-area padding without clipping the "Google Play"
          // text (which sits ~13% from the PNG's right edge). The auto
          // height preserves aspect so the badge doesn't get squished.
          backgroundSize: '124% auto',
        }}
      />
    </Box>
  );
}

AppStoreBadges.propTypes = {
  sx: PropTypes.object,
  scale: PropTypes.number,
  children: PropTypes.node,
};
